import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchFeeCalculationsReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { FeeCalculationsReportApiItem, ReportApiDateRangeQuery } from '../../types/report';
import { getLast30DaysInclusiveRange } from '../../utils/reportDateRanges';
import { gridFormatDecimalString, gridFormatDecimalString4 } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

/** Same cap as the fee service: the CSV is built in one request. */
const MAX_EXPORT_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

function exportRangeProblem(range: ReportApiDateRangeQuery | null) {
  if (!range?.startDate || !range?.endDate) return 'Choose a From and a To date to export.';

  const days = (Date.parse(`${range.endDate}T00:00:00Z`) - Date.parse(`${range.startDate}T00:00:00Z`)) / DAY_MS + 1;

  return days > MAX_EXPORT_DAYS ? `Choose a range of at most ${MAX_EXPORT_DAYS} days to export.` : null;
}

function feeCalculationsRowId(row: FeeCalculationsReportApiItem) {
  return String(row.id);
}

export function FeeCalculationsReportPage() {
  // The page opens on the same "Monthly" period the filter shows, so the first fetch is already date-bound.
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(() => getLast30DaysInclusiveRange());
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 100 },
    { field: 'product_id', headerName: 'Product Id', type: 'number', width: 110 },
    { field: 'product_name', headerName: 'Product name', minWidth: 180, flex: 0.3, sortable: false },
    { field: 'fee_config_id', headerName: 'Fee config Id', type: 'number', width: 120, sortable: false },
    {
      field: 'total_market_value',
      headerName: 'Total market value',
      width: 160,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'calculated_total_fee',
      headerName: 'Calculated total fee',
      width: 170,
      sortable: false,
      valueFormatter: gridFormatDecimalString4,
    },
    { field: 'actual_total_fee', headerName: 'Actual total fee', width: 150, valueFormatter: gridFormatDecimalString4 },
    {
      field: 'management_fee',
      headerName: 'Management fee',
      width: 150,
      sortable: false,
      valueFormatter: gridFormatDecimalString4,
    },
    { field: 'vat', headerName: 'VAT', width: 110, sortable: false, valueFormatter: gridFormatDecimalString4 },
    { field: 'date', headerName: 'Date', width: 170 },
    { field: 'type', headerName: 'Type', width: 170 },
    { field: 'status', headerName: 'Status', width: 130 },
  ]);

  const { feeCalculationsReportItems, totalFeeCalculationsReportItems, fetchFeeCalculationsReportStatus, error } =
    useSelector(reportSelector);

  const exportProblem = exportRangeProblem(dateRange);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'fee-calculations-report.csv',
    useCallback(() => reportService.exportFeeCalculationsReportCsv(dateRange ?? {}), [dateRange]),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} initialPreset="monthly" />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Fee calculations</Typography>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            {exportProblem && (
              <Typography variant="caption" color="text.secondary">
                {exportProblem}
              </Typography>
            )}
            <LoadingButton
              type="button"
              variant="outlined"
              color="primary"
              endIcon={<DownloadIcon />}
              loading={exportCsvLoading}
              loadingPosition="end"
              disabled={Boolean(exportProblem)}
              onClick={exportCsv}
            >
              Export CSV
            </LoadingButton>
          </Stack>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchFeeCalculationsReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={feeCalculationsReportItems}
          columns={columns.current}
          getRowId={feeCalculationsRowId}
          loading={fetchFeeCalculationsReportStatus === fetchStatuses.pending}
          rowCount={totalFeeCalculationsReportItems}
          initialSortModel={[{ field: 'date', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
